import { useEffect, useRef } from 'react';
import type { ResolvedTheme } from './theme';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme: ResolvedTheme;
          action: string;
          size: 'flexible';
          callback: (token: string) => void;
          'expired-callback': () => void;
          'error-callback': () => void;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const scriptId = 'aot-turnstile-script';

export function TurnstileWidget({
  siteKey,
  action,
  theme,
  onToken,
}: {
  siteKey?: string | null;
  action: 'login' | 'password_reset' | 'mfa_challenge' | 'invitation';
  theme: ResolvedTheme;
  onToken: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey || !container.current) return;
    let widgetId: string | undefined;
    let cancelled = false;
    const render = () => {
      if (cancelled || !container.current || !window.turnstile) return;
      widgetId = window.turnstile.render(container.current, {
        sitekey: siteKey,
        theme,
        action,
        size: 'flexible',
        callback: onToken,
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (window.turnstile) render();
    else if (existing) existing.addEventListener('load', render, { once: true });
    else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', render, { once: true });
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      if (existing) existing.removeEventListener('load', render);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      onToken('');
    };
  }, [action, onToken, siteKey, theme]);

  if (!siteKey) return null;
  return <div className="turnstile-widget" ref={container} aria-label="Security verification" />;
}
